import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Bridges a notification click (handled by the global header) to the global, read-only
 * Approval Request Details dialog. The dialog opens in place on whatever page the user is
 * on — clicking a notification never navigates to the approvals / requests screen.
 */
/** Payload for opening the global request-detail dialog. */
export interface OpenRequestOptions {
  requestId: number;
  /** Optional dialog header override (defaults to "Approval Request Details"). */
  title?: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationActionService {
  private readonly openRequestSubject = new Subject<OpenRequestOptions>();
  private readonly requestsChangedSubject = new Subject<void>();

  /** The global request-detail dialog subscribes to open for a given approval request id. */
  readonly openRequest$ = this.openRequestSubject.asObservable();

  /**
   * Emits after an approve / reject / return done from the global dialog, so any open
   * approvals / requests list can refresh its rows.
   */
  readonly requestsChanged$ = this.requestsChangedSubject.asObservable();

  /**
   * Open the request-detail dialog for the given request id. Pass an optional
   * `title` to override the dialog header (e.g. "Request Details" on the maker's
   * Employee Requested List); omit it to use the default "Approval Request Details".
   */
  openRequest(requestId: number, title?: string): void {
    this.openRequestSubject.next({ requestId, title });
  }

  /** Notify open lists that a request changed and should be reloaded. */
  requestsChanged(): void {
    this.requestsChangedSubject.next();
  }
}
